import { applyMask, buildMaskVars } from './naming.ts';
import {
  formatNamingProfile,
  resolveNamingProfileVersion,
  type NamingProfileAssignments,
  type NamingProfileCatalog,
} from './namingProfile.logic.ts';

type RackLike = {
  id: string;
  label?: string;
  rowId?: string;
  rackPosInRow?: number;
  rowIndex: number;
  rackIndex: number;
  x: number;
};

type RowLike = { id: string; labelIndex: number; moduleId?: string | null };

export type RenumberPlanEntry = {
  rackId: string;
  oldAddress: string;
  newAddress: string;
  rowIndex: number;
  rackIndex: number;
  rackPosInRow: number;
  conflicts: string[];
};

export function buildRenumberPlan(opts: {
  racks: Record<string, RackLike>;
  rows: Record<string, RowLike>;
  mask: string;
  module: string;
  hall: string;
  namingProfiles?: NamingProfileCatalog;
  namingAssignments?: NamingProfileAssignments;
}): RenumberPlanEntry[] {
  const rowOrder = Object.values(opts.rows).sort((a, b) => a.labelIndex - b.labelIndex);
  const rowById = new Map(rowOrder.map((row, index) => [row.id, index]));
  const racksByRow = new Map<string, RackLike[]>();

  Object.values(opts.racks).forEach(rack => {
    const key = rack.rowId || '__unassigned';
    const rowRacks = racksByRow.get(key) ?? [];
    rowRacks.push(rack);
    racksByRow.set(key, rowRacks);
  });

  const plan: RenumberPlanEntry[] = [];
  [...racksByRow.entries()]
    .sort(([leftId], [rightId]) => {
      const left = leftId === '__unassigned' ? Number.MAX_SAFE_INTEGER : (rowById.get(leftId) ?? Number.MAX_SAFE_INTEGER);
      const right = rightId === '__unassigned' ? Number.MAX_SAFE_INTEGER : (rowById.get(rightId) ?? Number.MAX_SAFE_INTEGER);
      return left - right;
    })
    .forEach(([rowId, rowRacks]) => {
    rowRacks.sort((a, b) => {
      const positionDiff = (a.rackPosInRow ?? Infinity) - (b.rackPosInRow ?? Infinity);
      return positionDiff || a.x - b.x;
    });
    const rowMeta = rowId === '__unassigned' ? undefined : opts.rows[rowId];
    const rowIndex = rowMeta ? rowMeta.labelIndex - 1 : (rowRacks[0]?.rowIndex ?? 0);
    const operatorRow = rowMeta?.labelIndex ?? rowIndex + 1;

    rowRacks.forEach((rack, index) => {
      plan.push({
        rackId: rack.id,
        oldAddress: currentAddress(opts, rack),
        newAddress: formatAddress(opts, rowId, operatorRow, index + 1),
        rowIndex,
        rackIndex: index,
        rackPosInRow: index + 1,
        conflicts: [],
      });
    });
    });

  const entriesByAddress = new Map<string, RenumberPlanEntry[]>();
  plan.forEach(entry => {
    const key = entry.newAddress.trim().toLocaleLowerCase();
    const entries = entriesByAddress.get(key) ?? [];
    entries.push(entry);
    entriesByAddress.set(key, entries);
  });
  entriesByAddress.forEach((entries, address) => {
    if (entries.length < 2) return;
    entries.forEach(entry => entry.conflicts.push(`Адрес ${address} назначен нескольким стойкам`));
  });

  const racksByAddress = new Map<string, RackLike[]>();
  Object.values(opts.racks).forEach(rack => {
    const key = currentAddress(opts, rack).trim().toLocaleLowerCase();
    const racks = racksByAddress.get(key) ?? [];
    racks.push(rack);
    racksByAddress.set(key, racks);
  });
  plan.forEach(entry => {
    const owners = racksByAddress.get(entry.newAddress.trim().toLocaleLowerCase()) ?? [];
    owners
      .filter(owner => owner.id !== entry.rackId)
      .forEach(owner => entry.conflicts.push(`Адрес ${entry.newAddress} уже занят стойкой ${owner.id}`));
  });
  plan.forEach(entry => {
    entry.conflicts = [...new Set(entry.conflicts)];
  });
  return plan;
}

function currentAddress(opts: Parameters<typeof buildRenumberPlan>[0], rack: RackLike): string {
  if (rack.label?.trim()) return rack.label.trim();
  try {
    const row = rack.rowId ? opts.rows[rack.rowId]?.labelIndex : undefined;
    return formatAddress(
      opts,
      rack.rowId || '__unassigned',
      row ?? rack.rowIndex + 1,
      rack.rackPosInRow ?? rack.rackIndex + 1,
    );
  } catch {
    return `rack-${rack.id}`;
  }
}

function formatAddress(
  opts: Parameters<typeof buildRenumberPlan>[0],
  rowId: string,
  row: number,
  rack: number,
): string {
  if (opts.namingProfiles && opts.namingAssignments) {
    const rowMeta = opts.rows[rowId];
    const module = Number(opts.module);
    if (Number.isInteger(module) && module > 0) {
      const profile = resolveNamingProfileVersion(opts.namingProfiles, opts.namingAssignments, {
        rowId: rowId === '__unassigned' ? undefined : rowId,
        moduleId: rowMeta?.moduleId ?? undefined,
      });
      return formatNamingProfile(profile.profile, { module, row, rack });
    }
  }
  return applyMask(opts.mask, buildMaskVars({ module: opts.module, hall: opts.hall, row, rack }));
}
